import {
  createBrowserRouter,
  createHashRouter,
  Navigate,
  type RouteObject,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import HowItWorks from "./pages/header_menu/HowItWorks";
import Layout from "./components/Layout";
import { CelebrationProvider } from "./context/CelebrationContext";
import Auth from "./pages/Auth";
import Auctions from "./pages/Auctions";
const CampaignDetails = lazy(() => import("./pages/CampaignDetails"));
import MaskData from "./pages/MaskData";
import CarDetails from "./pages/CarDetails";
import Dashboard from "./pages/Dashboard";
import Orders from "./pages/Orders";
import Settings from "./pages/Settings";
import Support from "./pages/Support";
import Index from "./pages/Index";
import CarTest from "./pages/CarTest";
import Favorites from "./pages/Favorites";
import SellCar from "./pages/header_menu/SellCar";
import AdminLeadbox from "./admin/pages/AdminLeadbox";
import CarCardPage from "./admin/pages/CarCardPage";
import MCLeads from "./admin/pages/MCLeads";
import LeadDetailsPage from "./admin/pages/LeadDetailsPage";
import VehicleCardListPage from "./admin/pages/VehicleCardListPage";
import AdminSupport from "./admin/pages/AdminSupport";
import SupportTicketPage from "./admin/pages/SupportTicketPage";
import AdminEconomy from "./admin/pages/AdminEconomy";
import AdminCarTest from "./admin/pages/AdminCarTest";
import AdminCarTestArchive from "./admin/pages/AdminCarTestArchive";
import CarTestArchive from "./pages/CarTestArchive";
import { SettingsTabs } from "./components/settings/SettingsTabs";
import ListOrValueCar from "./pages/ListOrValueCar";
import CarValuation from "./pages/CarValuation";
import CarValuationEmbed from "./pages/CarValuationEmbed";
import ListingsEmbed from "./pages/ListingsEmbed";
import CarPostAnAd from "./pages/CarPostAnAd";
import MyAuctions from "./pages/MyAuctions";
import { UserType } from "./types/user";
import { ProtectedRoute } from "./context/ProtectedRoute";
import { AdminPermissionGate } from "./context/AdminPermissionGate";
import NotFound from "./pages/NotFound";
import RouteErrorFallback from "./components/native/RouteErrorFallback";
import EmailVerification from "./pages/EmailVerification";
import AcceptTerms from "./pages/AcceptTerms";
import ResetPassword from "./pages/ResetPassword";
import AdminListings from "./admin/pages/AdminListings";
import AdminFeedback from "./admin/pages/AdminFeedback";
import Exterior360Test from "@/pages/Exterior360Test";
import Notifications from "./pages/Notifications";
import BuyCarAuctions from "./pages/header_menu/BuyCarAuctions";
import DiditSuccess from "./pages/DiditSuccess";
import DiditCallback from "./pages/DiditCallback";
import DealerPortal from "./pages/DealerPortal";
import DealerPortalVehicleCardPage from "./pages/DealerPortalVehicleCardPage";
import PrivacyPolicies from "./pages/PrivacyPolicies";
import Contact from "./pages/Contact";
import { NativeListingsSkeleton } from "./components/native/NativeListingsSkeleton";

const isCapacitorBuild = import.meta.env.MODE === "capacitor";

const appRoutes: RouteObject[] = [
  {
    path: "/",
    element: (
      <CelebrationProvider>
        <Layout />
      </CelebrationProvider>
    ),
    children: [
      // Public routes (no protection needed)
      {
        index: true,
        element: <Index />,
      },
      {
        path: "hur-det-fungerar",
        element: <HowItWorks />,
      },
      {
        path: "how-it-works",
        element: <Navigate to="/hur-det-fungerar" replace />,
      },
      {
        path: "auth",
        element: <Auth />,
      },
      {
        path: "verify-email",
        element: <EmailVerification />,
      },
      {
        path: "accept-terms",
        element: <AcceptTerms />,
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "mask-data",
        element: <MaskData />,
      },
      {
        path: "privacy",
        element: <PrivacyPolicies />,
      },
      {
        path: "contact",
        element: <Contact />,
      },
      {
        path: "salj-bilen",
        element: <SellCar />,
      },
      {
        path: "sell-car",
        element: <Navigate to="/salj-bilen" replace />,
      },
      {
        path: "kop-bil",
        element: <BuyCarAuctions />,
      },
      {
        path: "buy-car",
        element: <Navigate to="/kop-bil" replace />,
      },
      {
        path: "car-valuation-embed",
        element: <CarValuationEmbed />,
      },
      {
        path: "listings-embed",
        element: <ListingsEmbed />,
      },
      {
        path: "/kyc/success",
        element: <DiditSuccess />,
      },
      {
        path: "/kyc/callback",
        element: <DiditCallback />,
      },

      // Protected routes for all authenticated users
      {
        path: "dashboard",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Admin,
              UserType.Individual,
            ]}
          >
            <Dashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: "list-or-value-car",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Individual,
              UserType.Admin,
            ]}
          >
            <ListOrValueCar />
          </ProtectedRoute>
        ),
      },
      {
        path: "car-valuation",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Individual,
              UserType.Admin,
            ]}
          >
            <CarValuation />
          </ProtectedRoute>
        ),
      },
      {
        path: "car-post-an-ad",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Individual,
              UserType.Admin,
            ]}
          >
            <CarPostAnAd />
          </ProtectedRoute>
        ),
      },
      {
        path: "notifications",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Admin,
              UserType.Individual,
            ]}
          >
            <Notifications />
          </ProtectedRoute>
        ),
      },
      {
        path: "my-auctions",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Individual,
            ]}
          >
            <MyAuctions />
          </ProtectedRoute>
        ),
      },
      {
        path: "favorites",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Individual,
            ]}
          >
            <Favorites />
          </ProtectedRoute>
        ),
      },

      // Admin only routes
      {
        path: "admin/leadbox",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <AdminLeadbox />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/leads/:id",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <LeadDetailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/leads/mc",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <MCLeads />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/leadbox/vehicle-cards",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <VehicleCardListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/leadbox/vehicle-cards/:id",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <CarCardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/leadbox/listings",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <AdminPermissionGate permission="canViewBidsOverview">
              <AdminListings />
            </AdminPermissionGate>
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/support",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <AdminPermissionGate permission="canManageSupport">
              <AdminSupport />
            </AdminPermissionGate>
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/support/:id",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <AdminPermissionGate permission="canManageSupport">
              <SupportTicketPage />
            </AdminPermissionGate>
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/economy",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <AdminEconomy />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/car-test",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <AdminCarTest />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/car-test/archive",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <AdminCarTestArchive />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/feedback",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <AdminPermissionGate permission="canManageSupport">
              <AdminFeedback />
            </AdminPermissionGate>
          </ProtectedRoute>
        ),
      },
      // Admin-only: 360 exterior view POC (no UI link, access via URL)
      {
        path: "360-test",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Admin]}>
            <Exterior360Test />
          </ProtectedRoute>
        ),
      },

      {
        path: "car-test",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Individual,
            ]}
          >
            <CarTest />
          </ProtectedRoute>
        ),
      },
      {
        path: "car-test/archive",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Individual,
            ]}
          >
            <CarTestArchive />
          </ProtectedRoute>
        ),
      },
      {
        path: "auctions/all",
        element: <Auctions />,
      },
      {
        path: "auctions",
        element: <Auctions />,
      },
      {
        path: "auctions/:campaignId",
        element: (
          <Suspense fallback={<NativeListingsSkeleton count={6} />}>
            <CampaignDetails />
          </Suspense>
        ),
      },
      {
        path: "listings/:listingId",
        element: <CarDetails />,
      },
      {
        // Legacy: /auctions/:campaignId/:listingId → still works (CarDetails soft-redirects to /listings/:slug)
        path: "auctions/:campaignId/:listingId",
        element: <CarDetails />,
      },
      {
        path: "orders",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Individual,
            ]}
          >
            <Orders />
          </ProtectedRoute>
        ),
      },

      // Settings routes (available to all authenticated users)
      {
        path: "settings",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Admin,
              UserType.Individual,
            ]}
          >
            <Settings />
          </ProtectedRoute>
        ),
      },
      {
        path: "profile-settings",
        element: <Navigate to="/settings?tab=profile" replace />,
      },
      {
        path: "support",
        element: (
          <ProtectedRoute
            allowedUserTypes={[
              UserType.Business,
              UserType.Dealer,
              UserType.Individual,
            ]}
          >
            <Support />
          </ProtectedRoute>
        ),
      },
      // Dealer only: Handlarportalen
      {
        path: "dealer-portal",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Dealer]}>
            <DealerPortal />
          </ProtectedRoute>
        ),
      },
      {
        path: "dealer-portal/vehicles/:vehicleId",
        element: (
          <ProtectedRoute allowedUserTypes={[UserType.Dealer]}>
            <DealerPortalVehicleCardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
    errorElement: <RouteErrorFallback />,
  },
];

export const router = isCapacitorBuild
  ? createHashRouter(appRoutes)
  : createBrowserRouter(appRoutes);
